import {createApi} from "@reduxjs/toolkit/query/react"
import {IBasicResponse} from "../../types/api"
import baseQuery from "../../utils/apiIIKOConfig"
import {
    IDeletePromotionParams,
    IBackfillSegmentParams,
    ICreatePromotionResponse,
    ICreateSegmentResponse,
    IGetSegmentParams,
    IDeleteSegmentParams,
    ISegmentDetailsResponse,
    ISegmentListResponse,
    ISegmentRuleTypesResponse,
    ISegmentSavePayload,
    IUpdateSegmentParams,
    IPromotionListParams,
    IPromotionListResponse,
    IPromotionSavePayload,
    IUpdatePromotionParams
} from "./PromotionApiTypes"

export const promotionApi = createApi({
    reducerPath: "promotionApi",
    baseQuery,
    tagTypes: ["promotions", "segments", "segmentRuleTypes"],
    endpoints: (build) => ({
        getPromotions: build.query<IPromotionListResponse, IPromotionListParams>({
            query: (body) => ({
                url: "/v1/promotions/list",
                method: "POST",
                body
            }),
            providesTags: ["promotions"]
        }),
        createPromotion: build.mutation<IBasicResponse<ICreatePromotionResponse>, IPromotionSavePayload>({
            query: (body) => ({
                url: "/v1/promotions",
                method: "POST",
                body
            }),
            invalidatesTags: ["promotions"]
        }),
        updatePromotion: build.mutation<void, IUpdatePromotionParams>({
            query: ({id, ...body}) => ({
                url: `/v1/promotions/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["promotions"]
        }),
        deletePromotion: build.mutation<void, IDeletePromotionParams>({
            query: ({id}) => ({
                url: `/v1/promotions/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["promotions"]
        }),
        getSegmentsRuleTypes: build.query<ISegmentRuleTypesResponse, void>({
            query: () => ({
                url: "/v1/segments/rule-types",
                method: "GET",
            }),
            providesTags: ["segmentRuleTypes"]
        }),
        getSegments: build.query<ISegmentListResponse, void>({
            query: () => ({
                url: "/v1/segments",
                method: "GET",
            }),
            providesTags: ["segments"]
        }),
        getSegmentById: build.query<ISegmentDetailsResponse, IGetSegmentParams>({
            query: ({id}) => ({
                url: `/v1/segments/${id}`,
                method: "GET",
            }),
            providesTags: (_result, _error, params) => [{type: "segments", id: params.id}]
        }),
        createSegment: build.mutation<IBasicResponse<ICreateSegmentResponse>, ISegmentSavePayload>({
            query: (body) => ({
                url: "/v1/segments",
                method: "POST",
                body
            }),
            invalidatesTags: ["segments"]
        }),
        updateSegment: build.mutation<void, IUpdateSegmentParams>({
            query: ({id, ...body}) => ({
                url: `/v1/segments/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["segments"]
        }),
        deleteSegment: build.mutation<void, IDeleteSegmentParams>({
            query: ({id}) => ({
                url: `/v1/segments/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["segments"]
        }),
        backfillSegment: build.mutation<void, IBackfillSegmentParams>({
            query: ({id}) => ({
                url: `/v1/segments/${id}/backfill`,
                method: "POST",
            }),
            invalidatesTags: ["segments"]
        })
    })
})

export const {
    useGetPromotionsQuery,
    useGetSegmentsRuleTypesQuery,
    useGetSegmentsQuery,
    useLazyGetSegmentByIdQuery,
    useCreateSegmentMutation,
    useUpdateSegmentMutation,
    useDeleteSegmentMutation,
    useBackfillSegmentMutation,
    useCreatePromotionMutation,
    useUpdatePromotionMutation,
    useDeletePromotionMutation
} = promotionApi
